// Modern ArcGIS 4.31 imports
import Geometry from "@arcgis/core/geometry/Geometry";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import Color from "@arcgis/core/Color";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import OAuthInfo from "@arcgis/core/identity/OAuthInfo";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import Legend from "@arcgis/core/widgets/Legend";
import moment from "moment";
import Database_Local from "./Database_Local";

export type SimpleFillStyle =
    | "solid"
    | "none"
    | "horizontal"
    | "vertical"
    | "forward-diagonal"
    | "backward-diagonal"
    | "cross"
    | "diagonal-cross";

export type MapItemPopup = {
    id: string;
    name: string;
    title: string;
    content: string;
    hasAction: boolean;
    actionType?: "edit" | "zoom";
};

export type MapItem = {
    geometry: Geometry;
    popup?: MapItemPopup;
    attributes?: any;
    color?: Color;
    style?: SimpleFillStyle;
};

const createMapItem = (
    type: "polygon" | "polyline",
    rings: number[][][],
    itemPopup?: MapItemPopup,
    itemAttributes?: any,
    color?: Color,
    style?: SimpleFillStyle
): MapItem => {
    let geometry: Geometry;

    if (type === "polygon") {
        geometry = new Polygon({
            rings: rings,
            spatialReference: { wkid: 4326 },
        });
    } else {
        geometry = new Polyline({
            paths: rings,
            spatialReference: { wkid: 4326 },
        });
    }

    return {
        geometry,
        popup: itemPopup,
        attributes: itemAttributes,
        color,
        style,
    };
};

const configAuth = (authApiId: string, portalUrl: string, callbackUrl: string, token?: string): void => {
    console.log("ArcGIS Auth Config:", {
        appId: authApiId,
        portalUrl,
        callbackUrl,
        hasToken: !!token,
    });

    const info = new OAuthInfo({
        appId: authApiId,
        flowType: "implicit", // Use implicit flow to get access_token directly
        popup: true, // Use popup to avoid redirect URL issues
        portalUrl: portalUrl,
        popupCallbackUrl: callbackUrl,
    });

    IdentityManager.registerOAuthInfos([info]);

    if (token) {
        console.log("Registering existing token with IdentityManager");
        IdentityManager.registerToken({
            token: token,
            server: info.portalUrl,
        });
    }

    console.log("Auth configuration completed - IdentityManager will handle auth on-demand");
};

const createWebMap = (portalItemId: string) => {
    const webmap = new WebMap({
        portalItem: {
            id: portalItemId,
        },
    });

    const graphicsLayer = new GraphicsLayer({ title: "Permit Boundary" });

    webmap.when(() => {
        webmap.layers.add(graphicsLayer);
    });

    return { webmap, graphicsLayer };
};

const createMapView = (
    container: any,
    webmap: WebMap,
    showLegend: boolean,
    targets?: () => any,
    center?: Point,
    onPopupClick?: (id: string) => void
) => {
    const view = new MapView({
        map: webmap,
        center,
        zoom: 9,
        container: container,
        constraints: {
            snapToZoom: false,
        },
        spatialReference: { wkid: 3857 },
        // Let the WebMap determine the spatial reference automatically
    });

    view.when(() => {
        if (targets) {
            view.goTo(targets());
        }

        if (onPopupClick) {
            view.popup.on("trigger-action", (event: any) => {
                onPopupClick(event.action.id);
            });
        }

        const scalebar = new ScaleBar({
            view: view,
        });
        view.ui.add(scalebar, "bottom-left");

        if (showLegend) {
            const legend = new Legend({
                view: view,
            });
            view.ui.add(legend, "top-right");
        }
    });

    return view;
};

const addSimpleMarker = (mapview: MapView, point: Point, color?: Color) => {
    const simpleMarkerSymbol = new SimpleMarkerSymbol({
        color: color ? color : new Color([226, 119, 40]), // Orange
        outline: {
            color: new Color([255, 255, 255]), // White
            width: 1,
        },
    });

    // Add the geometry and symbol to a new graphic
    const graphic = new Graphic({
        geometry: point,
        symbol: simpleMarkerSymbol,
    });

    mapview.graphics.add(graphic);
};

const addPolyline = (graphicsLayer: GraphicsLayer, polyline: Polyline, color?: Color, popup?: MapItemPopup) => {
    const lineSymbol = new SimpleLineSymbol({
        color: color ? color : new Color([226, 119, 40]),
        width: "4px",
        style: "short-dash",
    });

    return addGraphic(lineSymbol, graphicsLayer, polyline, popup);
};

const addPolygon = (
    graphicsLayer: GraphicsLayer,
    polygon: Geometry,
    color?: Color,
    style?: SimpleFillStyle,
    popup?: MapItemPopup,
    attributes?: any
) => {
    const fillSymbol = new SimpleFillSymbol({
        style: style ? style : "solid",
        color: color
            ? new Color([color.r, color.g, color.b, style !== "solid" ? 0.5 : 0.15])
            : new Color([226, 119, 40, style !== "solid" ? 0.5 : 0.15]),
        outline: {
            color: color ? new Color([color.r, color.g, color.b, 1]) : new Color([226, 119, 40, 1]),
            width: 1,
        },
    });

    return addGraphic(fillSymbol, graphicsLayer, polygon, popup, attributes);
};

const addGraphic = (
    symbol: SimpleLineSymbol | SimpleFillSymbol,
    graphicsLayer: GraphicsLayer,
    geometry: Geometry,
    popup?: MapItemPopup,
    attributes?: any
) => {
    const graphic = new Graphic({
        geometry: geometry,
        symbol: symbol,
        // set attributes if provided
        ...(attributes && { attributes }),
    });

    if (popup) {
        const popupActions: any = popup.hasAction
            ? [
                  {
                      className: popup.actionType === "edit" ? "esri-icon-edit" : "esri-icon-zoom-in-magnifying-glass",
                      title: popup.name,
                      id: popup.id,
                  },
              ]
            : [];

        const popupTemplate = new PopupTemplate({
            title: popup.title,
            content: popup.content,
            actions: popupActions,
            overwriteActions: true,
        });

        graphic.popupTemplate = popupTemplate;
    }

    graphicsLayer.add(graphic);

    return graphic;
};

// Handle OAuth callback and set token
const handleArcGISAuthCallbackAndReload = (url: string): void => {
    try {
        console.log("Processing ArcGIS auth callback:", url);

        let searchParams: URLSearchParams;

        if (url.includes("#")) {
            // Parse fragment parameters (after #) which is common for implicit flow
            const fragment = url.split("#")[1];
            searchParams = new URLSearchParams(fragment);
        } else if (url.includes("?")) {
            // Parse query parameters (after ?)
            const query = url.split("?")[1];
            searchParams = new URLSearchParams(query);
        } else {
            console.log("No auth parameters found in URL");
            return;
        }

        // Handle implicit flow (access_token directly in URL)
        if (searchParams.has("access_token")) {
            const accessToken = searchParams.get("access_token");
            const expiresIn = searchParams.get("expires_in");

            if (accessToken) {
                console.log("Token extracted successfully, expires in:", expiresIn, "seconds");

                // Calculate expiration time
                const expirationSeconds = Number(expiresIn || 3600);
                const expiry = moment().add(expirationSeconds, "seconds").format();

                // Save to localStorage for persistence
                Database_Local.save("arcgis_token", accessToken);
                Database_Local.save("arcgis_expires_at", expiry);

                console.log("Token saved - reloading app to apply new credentials");

                // Reload the app - it will pick up the token from localStorage on restart
                window.location.reload();
            }
        } else if (searchParams.has("code")) {
            const code = searchParams.get("code");
            console.log("Authorization code received:", code);
            // Note: For authorization code flow, you typically need to exchange
            // the code for an access token on the server side
        }
    } catch (error: any) {
        console.error("Error handling ArcGIS auth callback:", error);
    }
};

export {
    configAuth,
    createMapView,
    createWebMap,
    addPolygon,
    addPolyline,
    addSimpleMarker,
    createMapItem,
    handleArcGISAuthCallbackAndReload,
};
